import { TimeFrameKey } from 'data/time-frames';
import { useMemo } from 'react';
import { GraphPoint } from 'services/generated';
import { useGetPortfolioPerformanceGraphQuery } from 'store/slices/gains-api';
import {
  useGetAvailableProductsGraphsQuery,
  useGetProductGraphQuery,
} from 'store/slices/products-api';
import { useGetProfileQuery } from 'store/slices/profile-api';
import { useGetRate, useGetRatesQuery } from 'store/slices/rates-api';
import { Rate } from 'store/slices/types/Rate';
import { normalizeValue } from 'utils/graph';
import { useProductsWithHoldingsAndRates } from './useProducts';

export function useProductsGraphs(
  timeFrame: TimeFrameKey,
  options?: { displayCurrency?: string; decimateBy?: number },
) {
  const { displayCurrency, decimateBy } = options ?? {};
  const {
    data: graphsData,
    isLoading: graphsLoading,
    isFetching: graphsFetching,
  } = useGetAvailableProductsGraphsQuery({
    timeFrame,
    displayCurrency,
    decimateBy,
  });
  const { data: profile, isLoading: isProfileLoading } = useGetProfileQuery();
  const { data: ratesData, isLoading: ratesLoading } = useGetRatesQuery({
    displayCurrency,
  });
  const isLoading = graphsLoading || ratesLoading || isProfileLoading;

  const graphs = useMemo(() => {
    if (!graphsData || !ratesData || !profile) {
      return undefined;
    }

    return Object.entries(graphsData.graphs).map(([productCode, graph]) => {
      // We add the most current rate here to make sure the graph is up to date
      const market = `${productCode}_${displayCurrency ?? profile.displayCurrency?.code}`;
      const productRate = ratesData.entities[market];
      return enrichGraph(productCode, graph, productRate);
    });
  }, [graphsData, ratesData, profile]);

  return { graphs, isLoading, isFetching: graphsFetching };
}

export function useProductGraph(
  productCode: string,
  timeFrame: TimeFrameKey,
  options?: { displayCurrency?: string; decimateBy?: number },
) {
  const { displayCurrency, decimateBy } = options ?? {};
  const { data: graphData, isLoading: graphsLoading } = useGetProductGraphQuery(
    { productCode, timeFrame, displayCurrency, decimateBy },
  );
  const { data: rates, isLoading: ratesLoading } = useGetRate(
    productCode,
    displayCurrency,
  );
  const isLoading = graphsLoading || ratesLoading;

  if (!graphData || !rates) {
    return { graph: undefined, isLoading };
  }

  const graph = enrichGraph(productCode, graphData.graph, rates);
  return { graph, isLoading };
}

export function usePortfolioPerformanceGraph(timeFrame: TimeFrameKey) {
  const { data: graphData, isLoading: graphsLoading } =
    useGetPortfolioPerformanceGraphQuery(timeFrame);
  const {
    totalPortfolioValue,
    lastChangedAt: portfolioLastChangedAt,
    isLoading: isProductsLoading,
  } = useProductsWithHoldingsAndRates();

  if (!graphData || totalPortfolioValue == null) {
    return { graph: undefined, isLoading: graphsLoading || isProductsLoading };
  }

  const dateParsedGraph = graphData.map(point => ({
    ...point,
    timestamp: new Date(point.timestamp * 1000),
  }));

  const fullGraph = [
    ...dateParsedGraph,
    {
      value: totalPortfolioValue,
      timestamp: portfolioLastChangedAt,
    },
  ];

  return { graph: fullGraph, isLoading: graphsLoading || isProductsLoading };
}

function enrichGraph(productCode: string, graph: GraphPoint[], rates: Rate) {
  const dateParsedGraph = graph.map(point => ({
    ...point,
    timestamp: new Date(point.timestamp * 1000),
  }));

  const fullGraph = [
    ...dateParsedGraph,
    ...(rates?.price && rates?.timestamp
      ? [
          {
            value: rates.price,
            timestamp: new Date(rates.timestamp),
          },
        ]
      : []),
  ];

  const maxValue = Math.max(...graph.map(point => point.value));
  const minValue = Math.min(...graph.map(point => point.value));
  const firstValue = fullGraph[0]?.value;
  const graphData = fullGraph.map(point => {
    const normalizedValue = normalizeValue(point.value, minValue, maxValue);
    const relativeChange = (point.value - firstValue) / firstValue;

    return {
      value: point.value,
      timestamp: point.timestamp,
      normalizedValue,
      relativeChange,
    };
  });
  return {
    productCode,
    maxValue,
    minValue,
    graph: graphData,
  };
}
